/**
 * RiskRuleThresholdEditDialog.tsx — 风控规则阈值编辑弹窗（ADM-USR-S007，注册名 risk-rule-edit）
 *
 * 1:1 移植自原型 design/Novel-admin/src/dialogs3.jsx 394–455（RiskRuleEditDialog）。
 * 原型用 condition 展示串正则反推阈值（mock 形态）；接真后改由真 DTO 条件三列驱动：
 * 仅阈值类维度（conditionOperator='gt' 标量，契约 §③）可编辑 condition_value，
 * 条件维度 / 运算符 / 执行动作只读。保存走 PATCH /api/v1/risk-rules/:id/threshold
 * （riskRulesApi.setThreshold + useMutation），成功按服务端真值 toast + invalidate
 * ['risk-rules']，失败错误 toast 不关弹窗（不假成功）。
 */

import { RISK_RULE_THRESHOLD_DIMENSIONS } from '@novel-admin/shared';
import { onlineManager, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { RiskRuleRow } from '~/data/queries';
import { reconcileRiskRuleThresholdCaches } from '~/data/risk-rule-threshold-cache';
import { riskRulesApi } from '~/hooks/use-risk-rules';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UiFormRow } from '~/ui/FormRow';
import { toast } from '~/ui/Toast';

// design dialogs.jsx 的薄包装：固定 dialogs 几何，与原型 1:1
const FormRow = (p: FormRowProps) => (
  <UiFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

/** 运算符机器码 → 展示符号（与 shared buildRiskConditionDisplay 同映射，只读展示用） */
const OPERATOR_DISPLAY: Record<string, string> = { eq: '==', in: 'IN', gt: '>' };

export function RiskRuleThresholdEditDialog({
  rule,
  onClose,
}: {
  rule: RiskRuleRow;
  onClose: () => void;
}) {
  // 可编辑性由真 DTO 维度判定（阈值类维度闭集，契约 §③）——不再用原型的展示串正则反推
  const editable = (RISK_RULE_THRESHOLD_DIMENSIONS as readonly string[]).includes(
    rule.conditionDimension,
  );
  const scalarValue = Array.isArray(rule.conditionValue) ? '' : String(rule.conditionValue);
  const [val, setVal] = useState(editable ? scalarValue : '');
  const opSymbol = OPERATOR_DISPLAY[rule.conditionOperator] ?? rule.conditionOperator;
  // 条件维度只读展示：从服务端派生 condition 展示串裁出维度段（与清单列同源），兜底机器码
  const dimensionDisplay = rule.condition.split(` ${opSymbol} `)[0] ?? rule.conditionDimension;

  const queryClient = useQueryClient();
  const saveMutation = useMutation({
    networkMode: 'always',
    retry: false,
    mutationFn: () => {
      if (!onlineManager.isOnline()) throw new Error('当前离线，请联网后重新确认操作');
      return riskRulesApi.setThreshold(rule.id, { condition_value: Number(val) });
    },
    // 照 SystemConfig 范式对账（adversarial F2）：adapt 返回 DTO → 同步 patch 共享缓存 → 再 await invalidate
    onSuccess: async (dto) => {
      // 包含inactive旧GET；禁止迟到响应覆盖ACK，也不回滚其他已确认的App开关/编辑元数据。
      await queryClient.cancelQueries({ queryKey: ['risk-rules'] }, { revert: false });
      // ACK先于刷新同步到每个实际scope缓存；GET失败也不能重新打开旧阈值。
      reconcileRiskRuleThresholdCaches(queryClient, dto);
      // toast 用服务端返回真值（更新后的 condition_value）
      toast(`已更新「${dto.name}」阈值 → ${String(dto.condition_value)} · 规则将在约 60 秒内生效`, {
        tone: 'pos',
        icon: 'IconCheck',
      });
      await queryClient.invalidateQueries({ queryKey: ['risk-rules'] });
      onClose();
    },
    // 失败 ≠ 一定没写入：refetch 对账服务端真值（不关弹窗、不假成功）
    onError: async (err) => {
      toast(`${rule.name} · ${err instanceof Error ? err.message : '阈值更新失败'}`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
      await queryClient.invalidateQueries({ queryKey: ['risk-rules'] });
    },
  });

  return (
    <Dialog
      width={560}
      title={`编辑风控规则 · ${rule.name}`}
      subtitle="超管专属 · 机密 · 仅可调整阈值标量 (condition_value) · 条件维度与运算符只读"
      icon="IconShield"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            <Icons.IconHistory size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            变更记入审计日志 · 规则将在约 60 秒内生效
          </div>
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button
            className="btn btn-accent"
            disabled={!editable || !val || saveMutation.isPending}
            onClick={() => {
              if (!onlineManager.isOnline()) {
                toast('当前离线，请联网后重新确认操作', { tone: 'neg' });
                return;
              }
              saveMutation.mutate();
            }}
          >
            <Icons.IconCheck size={11} />
            保存阈值
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <div
          className="card-flat"
          style={{ padding: 12, background: 'var(--bg-subtle)', marginBottom: 4 }}
        >
          <div className="hstack" style={{ gap: 8 }}>
            <span className="chip chip-neg" style={{ fontSize: 10 }}>
              <Icons.IconLock size={9} />
              超管专属 · 机密
            </span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
              {rule.id}
            </span>
            <div style={{ flex: 1 }} />
            <span
              className={`chip ${rule.level === 'high' ? 'chip-neg' : rule.level === 'medium' ? 'chip-warn' : ''}`}
              style={{ fontSize: 10 }}
            >
              {rule.level}
            </span>
          </div>
        </div>
        <FormRow label="条件维度" hint="只读">
          <input
            className="input mono"
            value={dimensionDisplay}
            disabled
            style={{ background: 'var(--bg-subtle)', maxWidth: 340 }}
          />
        </FormRow>
        <FormRow label="运算符" hint="只读">
          <input
            className="input mono"
            value={opSymbol}
            disabled
            style={{ background: 'var(--bg-subtle)', width: 90 }}
          />
        </FormRow>
        <FormRow
          label="阈值 condition_value"
          required
          hint={editable ? '本轮仅开放标量阈值编辑' : '该规则为集合 / 枚举条件 · 本轮不可编辑'}
        >
          {editable ? (
            <input
              className="input mono"
              value={val}
              onChange={(e) => setVal(e.target.value.replace(/[^\d]/g, ''))}
              style={{ width: 140, textAlign: 'right' }}
            />
          ) : (
            <input
              className="input mono"
              value={
                Array.isArray(rule.conditionValue)
                  ? `[${rule.conditionValue.join(', ')}]`
                  : String(rule.conditionValue)
              }
              disabled
              style={{ background: 'var(--bg-subtle)', maxWidth: 340 }}
            />
          )}
        </FormRow>
        <FormRow label="执行动作" hint="只读 · 由规则定义决定">
          <span
            className={`chip ${rule.action === '自动封禁' ? 'chip-neg' : 'chip-warn'}`}
            style={{ fontSize: 10.5 }}
          >
            {rule.action}
          </span>
        </FormRow>
        <div
          className="card-flat"
          style={{
            padding: '8px 12px',
            background: 'var(--info-soft)',
            borderColor: 'transparent',
            marginTop: 8,
          }}
        >
          <span style={{ fontSize: 11.5, color: 'var(--fg)' }}>
            <Icons.IconInfo
              size={11}
              style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--info)' }}
            />
            规则条件语义：{rule.name}（{rule.action}
            ）。命中账户记入审计日志，白名单设备不受固定设备规则影响。
          </span>
        </div>
      </div>
    </Dialog>
  );
}
